import { clearancePerStationScope, MAX_CLEARANCE_PAGES } from '@gis/clearance';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useStore } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ruleVars } from '@/lib/clearance/useClearanceVerdict';
import { toast } from 'sonner';

import { alignmentsAtom, alignmentWalkAtom, DEFAULT_WALK_STEP } from '@/atoms/alignment.atom';
import { clearanceEnvelopeVisibleAtom, clearanceViewModeAtom } from '@/atoms/clearance.atom';
import { clearanceViolationStepAtom, clearanceWalkTargetAtom } from '@/atoms/clearanceNavigation.atom';
import { layerDefaultSettings, layerSettingsAtom, layersAtom, type tLayerSettings } from '@/atoms/layers.atom';
import { usePointcloudHasClassification } from '@/atoms/pointcloudCapabilities.atom';
import {
  DOCK_VERY_NARROW_PX,
  stationClipToBinAtom,
  stationVolumeDockAtom,
  stationVolumeDockExpandedAtom,
  stationVolumeSelectionAtom,
} from '@/atoms/stationVolumes.atom';
import { clipBoxStateAtom, viewerEvents, type ClipBoxState } from '@/atoms/viewer.atom';
import { ClearanceChart } from '@/components/ClearanceChart';
import { ClearanceSectionChart } from '@/components/ClearanceSectionChart';
import { ClearanceProfileSketch } from '@/components/ClearanceProfileSketch';
import { cn } from '@/lib/utils';
import { ClearanceTable } from '@/components/ClearanceTable';
import { DockDetailsPopover, type DockDetailsSection } from '@/components/dock/DockDetailsPopover';
import { DockEmptyState, DockEmptyStateAction } from '@/components/dock/DockEmptyState';
import {
  DockExportMenu,
  DockMenuItem,
  DockMenuSeparator,
  DockOverflowMenu,
} from '@/components/dock/DockExportMenu';
import { DockBody, DockFrame } from '@/components/dock/DockFrame';
import {
  DOCK_CQ_HIDE,
  DockHeader,
  DockIdentity,
  DockKpiStrip,
  DockPanelButtons,
  type DockKpiItem,
} from '@/components/dock/DockHeader';
import { DockChip, DockKpi } from '@/components/dock/DockHeaderParts';
import { DockSelectionChip } from '@/components/dock/DockSelectionChip';
import { DockTabBar } from '@/components/dock/DockTabBar';
import { DockSegmented, DockStepper, DockToolbar, DockToolbarSeparator } from '@/components/dock/DockToolbar';
import { DockMenuRadioGroup, DockMenuSection, DockMenuSwitch, DockViewMenu } from '@/components/dock/DockViewMenu';
import { DOCK_TEXT } from '@/components/dock/dockStyles';
import { UpgradeBadge } from '@/components/subscription/FeatureGate';
import { VerdictChip } from '@/components/VerdictChip';
import { Button } from '@/components/ui/button';
import { LoadingBadge } from '@/components/ui/LoadingBadge';
import { getLinkSessionId } from '@/graphql/execute';
import { useClearance, useClearanceByproductUrl } from '@/hooks/useClearance';
import { useFeature } from '@/hooks/useFeature';
import type { OpenClearancePagesReportArgs, OpenClearanceReportArgs } from '@/hooks/useOpenAnalysisReport';
import { clipBoxAtStation, internalToDisplayRaw, type Alignment, type ClipDims } from '@/core/alignment';
import { openClearance } from '@/lib/analyses/openAnalysisDialogs';
import { clearanceClipDims, clearanceWorstTarget } from '@/lib/clearance/bands';
import {
  CLEARANCE_FILL,
  CLEARANCE_LEGEND_KEYS,
  CLEARANCE_LEGEND_ORDER,
  CLEARANCE_METRICS,
  defaultClearanceMetric,
  type ClearanceBarKind,
  type ClearanceMetric,
} from '@/lib/clearance/chart';
import { adjacentViolationBin, type ViolationStepDir } from '@/lib/clearance/navigation';
import { binAtStation } from '@/lib/clearance/table';
import { clearanceBinsToTsv } from '@/lib/clearance/tsv';
import type { ClearanceBin, DerivedClearanceLayerMetadata } from '@/lib/clearance/types';
import { useClearanceVerdict } from '@/lib/clearance/useClearanceVerdict';
import {
  CLEARANCE_VIEW_MODES,
  releaseViewMode,
  takeOverViewMode,
  viewModeUnavailableReason,
  type ClearanceViewMode,
  type ViewModeUnavailableReason,
} from '@/lib/clearance/viewMode';
import { binCentreStation, binIndexForStation } from '@/lib/stationVolumes/aggregate';
import { alignmentHashMatches } from '@/lib/stationVolumes/alignmentInput';
import { formatFixed, formatSigned, formatStationRange, staOnly, staTag } from '@/lib/stationVolumes/format';
import { lookAtSectionPose } from '@/lib/stationVolumes/sectionOverlay';
import { useDialog } from '@/providers/DialogProvider';

export interface ClearanceDockProps {
  /**
   * Which project's clip box "Clip to station" takes over
   * (`clipBoxStateAtom` is an atomFamily keyed by project, and the box is
   * persisted per project). It was carried unread until Task 6 for symmetry
   * with `StationVolumeDock`; it is load-bearing now, so it is required.
   */
  projectId: string;
  /**
   * The shared report handler (`useOpenAnalysisReport`, wired by `Viewer.tsx`
   * as `StationVolumeDock`'s is): Export ▾'s Summary and Per-station PDFs.
   * A prop rather than a closure so the path can be mounted end to end
   * (`clearanceReportReachable.dom.test.tsx`). The `report.pdf` paywall is the
   * handler's; the items' `disabled` is a data guard only.
   */
  onOpenPdfReport?: (args: OpenClearanceReportArgs | OpenClearancePagesReportArgs) => void;
}

/**
 * The one field this dock reads out of `useLayerSettings`' cached
 * `['layer-settings', id]` query — the renderer's own resolution of a layer's
 * remote settings (finding M9). Declared structurally rather than imported
 * from the generated operation types so that reading the cache pulls in no
 * GraphQL document; `getQueryData` is a typed cast either way.
 */
interface CachedLayerSettings {
  layer?: { settings?: tLayerSettings | null } | null;
}

/** Plot height used until the ResizeObserver reports — jsdom, and the first
 *  paint before layout has run. The dock's default body height minus its
 *  chrome; the measured value replaces it on the very next frame. */
const DEFAULT_PLOT_H = 180;
/** Below this plot width the static profile sketch stacks over its caption. */
const SKETCH_STACK_BELOW_PX = 560;
/** Room the stacked caption takes (two lines of text + the gap). */
const SKETCH_CAPTION_H = 44;

const TABS = ['chart', 'table', 'profile'] as const;

/** `useClearanceVerdict`'s input while no run is open — the hook cannot be
 *  skipped, and what it returns then is never rendered. */
const EMPTY_VERDICT_SUMMARY = {
  violationCount: 0,
  scoredPointCount: 0,
  nearMiss: { within0_1M: 0, within0_2M: 0, within0_35M: 0 },
};

/**
 * The 3D envelope's key, under the switch that summons it in View ▾ — the
 * sibling dock's `StationBandsLegend` rule (finding M5): the envelope's
 * per-interval tint is the chart's K2 ladder (`CLEARANCE_FILL`), but the chart
 * is on another tab half the time, and a 3D colour with its key out of sight
 * has no key. Named as well as coloured, as the chart's own key is; 11 px, the
 * dock's meta size, now that it has a menu row of its own to sit in.
 */
const EnvelopeKey = ({ band }: { band: string }) => {
  const { t } = useTranslation('project');
  // The plates are tinted by each interval's verdict, so the key names the
  // verdict, and its tooltip quotes the thresholds.
  const label = (kind: ClearanceBarKind) => t(CLEARANCE_LEGEND_KEYS[kind]);
  return (
    <div
      data-testid='clearance-envelope-key'
      title={t('clearance.table.verdictHelp', ruleVars(band))}
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${DOCK_TEXT.meta} text-slate-600 dark:text-stone-300`}
    >
      {CLEARANCE_LEGEND_ORDER.map((kind) => (
        <span key={kind} className='flex items-center gap-1'>
          <span
            aria-hidden
            data-testid={`clearance-envelope-key-${kind}`}
            className='inline-block h-2 w-3 rounded-[2px]'
            style={{ background: CLEARANCE_FILL[kind] }}
          />
          {label(kind)}
        </span>
      ))}
    </div>
  );
};

/** Label and tooltip per render mode. A map rather than a template string so
 *  every key is greppable and the locale parity test can see all six. */
const VIEW_MODE_KEYS = {
  scalar: { label: 'clearance.panel.viewModeScalar', help: 'clearance.panel.viewModeScalarHelp' },
  highlight: { label: 'clearance.panel.viewModeHighlight', help: 'clearance.panel.viewModeHighlightHelp' },
  only: { label: 'clearance.panel.viewModeOnly', help: 'clearance.panel.viewModeOnlyHelp' },
  // `as const` so the keys stay literals and `t()` can still check them —
  // widening them to `string` is what makes i18next's typed `t` reject the
  // lookup (the same shape `CLEARANCE_LEGEND_KEYS` uses).
} as const satisfies Record<ClearanceViewMode, { label: string; help: string }>;

/** Why Highlight / Violations-only cannot be offered, in the reader's words.
 *  A literal map for the same reason `VIEW_MODE_KEYS` is one — a template
 *  literal key does not typecheck against i18next's generated key union. */
const VIEW_MODE_UNAVAILABLE_KEYS = {
  loading: 'clearance.panel.viewModeUnavailable.loading',
  noClassification: 'clearance.panel.viewModeUnavailable.noClassification',
} as const satisfies Record<ViewModeUnavailableReason, string>;

/** The bar-height control's three options: short label in the segment, the
 *  long name as its tooltip. A literal map so every key typechecks. */
const METRIC_KEYS = {
  count: { label: 'clearance.chart.metricCountShort', help: 'clearance.chart.metricCount' },
  depth: { label: 'clearance.chart.metricDepthShort', help: 'clearance.chart.metricDepth' },
  nearMiss: { label: 'clearance.chart.metricNearMissShort', help: 'clearance.chart.metricNearMiss' },
} as const satisfies Record<ClearanceMetric, { label: string; help: string }>;

/**
 * Review finding 14: a failed fetch printed the raw `Error.message` — an
 * English GraphQL / HTTP string — inside localized UI. The failures this dock
 * can meet are few and known (`useClearance` / `useClearanceByproductUrl`), so
 * each maps to a translated headline; the raw message is kept, but only as
 * the tooltip, for whoever has to report it.
 */
type ClearanceLoadErrorKey =
  | 'clearance.dock.error.http'
  | 'clearance.dock.error.missing'
  | 'clearance.dock.error.network'
  | 'clearance.dock.error.corrupt'
  | 'clearance.dock.error.unknown';
const describeLoadError = (error: Error): { key: ClearanceLoadErrorKey; status?: string } => {
  const msg = error.message ?? '';
  // `fetchWithProgress`: `${res.status} ${res.statusText}`.
  const http = /^(?:HTTP\s+)?(\d{3})\b/.exec(msg);
  if (http) return { key: 'clearance.dock.error.http', status: http[1] };
  // `useClearanceByproductUrl`: the batch resolved, the file is not on it.
  if (/^No byproduct registered/.test(msg)) return { key: 'clearance.dock.error.missing' };
  // `JSON.parse` on a truncated or non-JSON body.
  if (error.name === 'SyntaxError') return { key: 'clearance.dock.error.corrupt' };
  // `fetch` itself rejecting: offline, CORS, DNS — a TypeError in every browser.
  if (error.name === 'TypeError' || /failed to fetch|network/i.test(msg)) return { key: 'clearance.dock.error.network' };
  return { key: 'clearance.dock.error.unknown' };
};

/**
 * The clearance dock — a parallel component to `StationVolumeDock`, not a
 * `kind` prop on it (ruling R2). It reuses the shared dock atoms (so only one
 * dock is ever open and the height preference carries across), the `SV_TOKENS`
 * palette and the station formatters; it does NOT reuse the volume-typed
 * chart, table or section machinery.
 *
 * Tabs are Chart / Table / **Profile**. There is deliberately no Section tab:
 * the station Section tab is built on `station_offsets.f32`, which a clearance
 * run does not produce (ruling R1). The Profile tab is the spec's own named
 * fallback and carries the same information the overlay was for — where in the
 * envelope the worst point sat.
 */
export const ClearanceDock = ({ projectId, onOpenPdfReport }: ClearanceDockProps) => {
  const { t } = useTranslation('project');
  const [dock, setDock] = useAtom(stationVolumeDockAtom);
  const [expanded, setExpanded] = useAtom(stationVolumeDockExpandedAtom);
  const [selection, setSelection] = useAtom(stationVolumeSelectionAtom);
  const layers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const [width, setWidth] = useState(1200);
  // Lifted out of `ClearanceChart` so switching to Table and back does not
  // silently reset the reader's chosen metric (L6). `null` = the reader has
  // not chosen: the run's own default applies (`defaultClearanceMetric` —
  // near misses on a run with no violation, whose Count bars are all zero),
  // and a PASS run with nothing to plot shows its empty state instead.
  // Reset per run (the `openClearanceLayer` effect), so a choice made on a
  // FAIL run does not open the next MARGINAL one on a chart of zeros.
  const [metricChoice, setMetricChoice] = useState<ClearanceMetric | null>(null);
  const [plotBox, setPlotBox] = useState<{ w: number; h: number } | null>(null);
  const sketchStacked = (plotBox?.w ?? width) < SKETCH_STACK_BELOW_PX;
  const gate = useFeature('analysis.clearance');
  // "Clip to station" takes the viewer's own clip box over, so it needs that
  // feature's tier as well as this analysis's — whichever half is locked is
  // the one the badge names.
  // A shared link gets no downloads, PDFs or re-runs: the CSV is a byproduct
  // the server withholds, and the rest are EDITOR jobs it can't start. The
  // clip box only changes the view, so it is never paywalled there (see
  // `clipBoxGate` in Viewer.tsx).
  const isSharedLink = !!getLinkSessionId();
  const clipFeature = useFeature('viewer.clipBox');
  const clipGate = isSharedLink ? { ...clipFeature, allowed: true } : clipFeature;
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  const [clipToBin, setClipToBin] = useAtom(stationClipToBinAtom(dock?.layerId ?? '__none'));
  // Keyed by layer, like the volumes bands: a preference set on one run must
  // not follow the reader to the next one. `__none` is the atomFamily's key
  // while no dock is open — the hook cannot be called conditionally.
  const [envelopeOn, setEnvelopeOn] = useAtom(clearanceEnvelopeVisibleAtom(dock?.layerId ?? '__none'));

  const isClearance = dock?.dataset === 'clearance';
  const layer = layers.find((l) => l.id === dock?.layerId);
  const layerMeta = (layer?.metadata ?? null) as DerivedClearanceLayerMetadata | null;
  const meta = layerMeta?.clearance ?? null;
  const batchId = layerMeta?.batchId;

  // "Which points violated" — the render mode, and the settings take-over that
  // shows it. The target is the DERIVED cloud (`dock.layerId`): that is the
  // layer carrying classification 13, not `meta.pointcloudLayerId`, which is
  // the untouched source survey the run was scored against (ruling P5).
  const store = useStore();
  const viewLayerId = isClearance && dock ? dock.layerId : null;
  const viewKey = viewLayerId ?? '__none';
  const [viewState, setViewState] = useAtom(clearanceViewModeAtom(viewKey));
  // Read/write the local OVERRIDE atom directly rather than through
  // `useLayerSettings`: that hook fires a per-layer GraphQL query, and this
  // component stays mounted with no dock open. `layersAtom` already carries
  // the remote settings, so the same `local || remote || default` resolution
  // costs nothing here.
  const [localSettings, setLocalSettings] = useAtom(layerSettingsAtom(viewKey));
  // Finding M9: the base a mode patches has to be the settings that are
  // ACTUALLY on screen, and the renderer (`Pointcloud` → `useLayerSettings`)
  // resolves those as `local || remote || default` where `remote` is the FULL
  // per-layer `layer-settings` query — not `layersAtom`'s selection, which is
  // a documented subset with the image-only fields left out.
  //
  // So read the renderer's own resolution out of the react-query cache under
  // its own key. A cache READ, deliberately: subscribing with `useQuery` would
  // fire a per-layer request from a component that stays mounted with no dock
  // open, and the value is only ever needed at the instant of a take-over.
  // `Pointcloud` has already populated it for any layer that is on screen; when
  // it has not (a hidden layer, a cold cache), the old merge is still the
  // fallback, so the base is never incomplete.
  const queryClient = useQueryClient();
  const resolveEffectiveSettings = (): tLayerSettings => {
    if (localSettings) return localSettings;
    const cached = viewLayerId
      ? queryClient.getQueryData<CachedLayerSettings>(['layer-settings', viewLayerId])?.layer?.settings
      : null;
    return cached ?? { ...layerDefaultSettings(layer), ...layer?.settings };
  };

  /**
   * Give the cloud back: on close, on a switch to another run, on unmount —
   * and on MOUNT, which is the one that matters most. `layerSettingsAtom` is
   * localStorage-backed, so a reload taken mid-mode leaves the repainted
   * settings persisted; the snapshot that undoes them is persisted beside it
   * (`clearanceViewModeAtom`), and this is where it gets spent.
   *
   * Everything goes through `store` rather than the render closure so the
   * cleanup restores the LATEST snapshot instead of one captured three
   * renders ago, and `releaseViewMode` owes no write at all when the dock
   * holds nothing — so running on every mount cannot stamp on settings this
   * dock never took.
   */
  useEffect(() => {
    if (!viewLayerId) return;
    const release = () => {
      const next = releaseViewMode(store.get(clearanceViewModeAtom(viewLayerId)));
      if (!next.write) return;
      store.set(layerSettingsAtom(viewLayerId), next.write.settings);
      store.set(clearanceViewModeAtom(viewLayerId), next.state);
    };
    release();
    return release;
  }, [viewLayerId, store]);

  /**
   * Finding I1: heal a take-over that a reload left stranded with NO dock open.
   *
   * The release above only runs for the layer whose dock is open, and
   * `stationVolumeDockAtom` is not persisted — so a reload taken in Highlight
   * or Violations-only comes back with the dock closed, the repainted patch
   * still in `layerSettingsAtom` (localStorage) and the snapshot that undoes it
   * unspent in `clearanceViewModeAtom`. The user saw a magenta (or, once the
   * cloud carries classification, a nearly empty) point cloud with nothing on
   * screen saying why.
   *
   * This component is mounted unconditionally by `Viewer` — it returns null
   * with no dock, it does not unmount — so it is the level that always runs.
   * Every clearance layer in the project except the one whose dock is open is
   * swept; `releaseViewMode` owes no write at all without a hold, so a layer
   * that was never taken over is never touched.
   */
  const clearanceLayerIds = useMemo(
    () =>
      layers
        .filter((l) => !!((l.metadata ?? null) as DerivedClearanceLayerMetadata | null)?.clearance)
        .map((l) => l.id)
        .join(','),
    [layers],
  );
  useEffect(() => {
    if (!clearanceLayerIds) return;
    for (const id of clearanceLayerIds.split(',')) {
      if (id === viewLayerId) continue;
      const next = releaseViewMode(store.get(clearanceViewModeAtom(id)));
      if (!next.write) continue;
      store.set(layerSettingsAtom(id), next.write.settings);
      store.set(clearanceViewModeAtom(id), next.state);
    }
  }, [clearanceLayerIds, viewLayerId, store]);

  /**
   * Does the cloud this dock would repaint actually carry a classification
   * attribute in its octree?
   *
   * `undefined` while it is still loading, `false` for every clearance cloud
   * converted before `processClearance` started asking the converter for
   * `Classification`. On such a cloud Potree hands the shader an all-zero
   * class buffer, so Highlight paints everything context-grey and
   * Violations-only draws nothing at all — a blank viewport with no
   * explanation. Keyed by the DERIVED layer (`viewLayerId`), the cloud that
   * carries class 13, exactly like `clearanceViewModeAtom` beside it.
   *
   * The atom, the detector and `viewModeUnavailableReason` are the
   * classification seam; this dock only consumes them.
   */
  const hasClassification = usePointcloudHasClassification(viewLayerId);
  const viewModeUnavailable = viewModeUnavailableReason(hasClassification);

  const setViewMode = (next: ClearanceViewMode) => {
    if (!viewLayerId) return;
    const step = takeOverViewMode(
      viewState,
      { local: localSettings, effective: resolveEffectiveSettings() },
      next,
      hasClassification,
    );
    setViewState(step.state);
    if (step.write) setLocalSettings(step.write.settings);
  };

  /**
   * A mode the reader chose (or a reload restored) before the octree landed,
   * on a cloud that turns out to carry no classification, has to undo itself
   * the moment that is known: `clearanceViewModeAtom` is localStorage-backed,
   * so otherwise the take-over survives with no control left that could exit
   * it. Deliberately keyed on `false` only — `undefined` is "not loaded yet",
   * and re-running this on the `undefined → true` flip would throw away a mode
   * the reader had just picked.
   */
  useEffect(() => {
    if (!viewLayerId || hasClassification !== false) return;
    const state = store.get(clearanceViewModeAtom(viewLayerId));
    if (state.mode === 'scalar' && !state.hold) return;
    const next = releaseViewMode(state);
    if (next.write) store.set(layerSettingsAtom(viewLayerId), next.write.settings);
    store.set(clearanceViewModeAtom(viewLayerId), next.state);
  }, [viewLayerId, hasClassification, store]);

  /**
   * Which radio is checked. A locked dock never takes the layer over, and
   * neither does one whose cloud cannot be repainted — in both cases the
   * cloud really is on the run's own ramp, so `scalar`, not "nothing
   * selected" (finding M6).
   */
  const selectedViewMode: ClearanceViewMode = gate.allowed && hasClassification !== false ? viewState.mode : 'scalar';
  // The radiogroup's keyboard (one tab stop, arrows move the check, muted
  // options skipped, a locked group prompting) is `DockMenuRadioGroup`'s now.

  const { data: json, isLoading, error, progress, refetch } = useClearance(meta, batchId);
  // Finding I3: `useClearance` throws a bare `SCHEMA_TOO_NEW` sentinel when the
  // byproduct was written by a newer worker than this client understands.
  // Printed raw it was an English constant in five languages, and retrying it
  // could never help — the file is not going to get older.
  const schemaTooNew = error?.message === 'SCHEMA_TOO_NEW';
  // Ruling: the CSV the worker already wrote is the export. Re-deriving one in
  // the browser would be a second implementation of the same table, free to
  // disagree with the file an engineer downloads from the batch panel.
  const csvFilename = isSharedLink ? null : meta?.byproducts?.csv;
  const csv = useClearanceByproductUrl(batchId, csvFilename);

  // The dock's own width, through a CALLBACK ref for the reason `plotRef`
  // below gives: the root only renders once `meta` is in hand, and an effect
  // keyed on the layer id ran while it was still null when the layer's
  // metadata landed after the dock opened — and never ran again.
  const rootObserverRef = useRef<ResizeObserver | null>(null);
  const rootRef = useCallback((el: HTMLDivElement | null) => {
    rootObserverRef.current?.disconnect();
    rootObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(w);
    });
    ro.observe(el);
    rootObserverRef.current = ro;
  }, []);
  useEffect(() => () => rootObserverRef.current?.disconnect(), []);

  // Measure the PLOT box, not the dock: the body is padded, the header wraps
  // to a second row at narrow widths, and the resize grip decides what is left
  // over. Arithmetic on `dock.height` cannot know any of that, and when it
  // guessed high the chart overflowed and the body grew a scrollbar
  // (`StationVolumeChart` measures for exactly this reason).
  //
  // A CALLBACK REF, not `useRef` + a `useEffect` keyed on the tab. Every pane
  // is rendered conditionally on `json`, which arrives from a fetch AFTER the
  // dock mounts — so the effect ran once with `plotRef.current === null`, took
  // its early return, and (its deps unchanged by the fetch resolving) never
  // ran again. The observer was never attached at all, and every pane drew at
  // the `DEFAULT_PLOT_H` fallback for the dock's whole life. That was invisible
  // while collapsed, because 180 px is about what a 280 px dock leaves over,
  // and unmissable expanded: a 180 px chart with the rest of the screen blank
  // under it. A callback ref fires on the NODE, so it attaches the moment the
  // pane appears and re-attaches when a tab switch swaps one pane for another.
  // Expanding needs nothing extra — the observed element itself changes size.
  const plotObserverRef = useRef<ResizeObserver | null>(null);
  const plotRef = useCallback((el: HTMLDivElement | null) => {
    plotObserverRef.current?.disconnect();
    plotObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r || !(r.width > 0) || !(r.height > 0)) return;
      setPlotBox((prev) => (prev && prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
    });
    ro.observe(el);
    plotObserverRef.current = ro;
  }, []);
  // React detaches the ref (calling it with null) when the dock unmounts, but
  // not when the whole tree is torn down mid-commit; one disconnect on unmount
  // means no observer ever outlives the element it was measuring.
  useEffect(() => () => plotObserverRef.current?.disconnect(), []);

  // Below 520 px a chart has no room; show the table rather than a chart
  // nobody can read.
  //
  // `isClearance` is part of the condition, not just of the render below: this
  // effect runs even while the component returns null, `width` is component
  // state that survives the dock closing, and the ResizeObserver stops
  // measuring while nothing is mounted. Without the guard, opening this dock in
  // a narrow window and later opening the VOLUMES dock in a wide one would
  // retab the volumes dock from a stale measurement (M8).
  useEffect(() => {
    if (!dock || !isClearance) return;
    if (width < DOCK_VERY_NARROW_PX && dock.tab === 'chart') setDock({ ...dock, tab: 'table' });
  }, [width, dock, isClearance, setDock]);

  // Drop the stale measurement when the dock closes, so the next open starts
  // from the observer's first real frame rather than from the last window size
  // this component happened to see.
  useEffect(() => {
    if (!isClearance) setWidth(1200);
  }, [isClearance]);

  const bins: ClearanceBin[] = useMemo(() => json?.bins ?? [], [json]);
  const sel = selection && dock && selection.layerId === dock.layerId ? selection : null;
  /**
   * The brush, only while it still covers something on screen. A brush left
   * behind by a longer run (or a refetch that shortened this one) survives in
   * the shared atom; with no bin under it, every place that keyed off `sel`
   * scoped the KPIs to NOTHING, hid the run verdict, and offered a "Clear"
   * for a selection the reader could not see. Every reader of the brush below
   * reads this, not `sel`.
   */
  //
  // Normalised to `fromBin <= toBin`: a drag made right-to-left writes the
  // ends the other way round, and every `>= from && <= to` test below would
  // otherwise match nothing and drop a brush that is plainly on screen.
  const selEff = useMemo(() => {
    if (!sel) return null;
    const fromBin = Math.min(sel.fromBin, sel.toBin);
    const toBin = Math.max(sel.fromBin, sel.toBin);
    return bins.some((b) => b.i >= fromBin && b.i <= toBin) ? { ...sel, fromBin, toBin } : null;
  }, [bins, sel]);

  const scoped = useMemo(
    () => (selEff ? bins.filter((b) => b.i >= selEff.fromBin && b.i <= selEff.toBin) : bins),
    [bins, selEff],
  );
  const totals = useMemo(() => {
    // Review finding 1: before the bins land — and for good, when the fetch
    // fails or the run has no JSON — `scoped` is empty, and a FAIL run's KPI
    // row read "Affected intervals 0" and "—" beside a FAIL chip. The layer
    // metadata carries the run summary the card already prints, so the
    // corridor-wide figures come from there. (No brush can be active with no
    // bins: `selEff` needs a bin under it.)
    const fallback = bins.length === 0 ? (json?.summary ?? meta?.summary ?? null) : null;
    if (fallback) {
      return {
        violations: fallback.violationCount,
        worst: fallback.maxIntrusionM,
        worstStationDisplayM: fallback.worstStationDisplayM,
        worstStationExact: true,
        worstStationRange: null,
        binsWithViolations: fallback.binsWithViolations,
        binCount: fallback.binCount,
        lengthM: null,
        scored: fallback.scoredPointCount,
      };
    }
    const violations = scoped.reduce((acc, b) => acc + b.violationCount, 0);
    const worstBin = scoped.reduce<ClearanceBin | null>(
      (acc, b) =>
        b.maxIntrusionM !== null && (acc === null || b.maxIntrusionM > (acc.maxIntrusionM ?? -Infinity)) ? b : acc,
      null,
    );
    // The worker recorded the EXACT station of the worst point, but only once,
    // for the run as a whole. Use it whenever the worst bin in view is that
    // same bin — quoting `1+030` (the bin midpoint) for a point the JSON says
    // sits at `1+031` would be the dock inventing a figure it was handed. Under
    // a brush that excludes the run's worst bin there is no per-bin equivalent
    // in the schema, so the midpoint is the best number available — and it is
    // marked `~` and given the bin's exact range as its title, because a figure
    // that can be half a bin out must not be printed as if it were exact.
    const summaryWorst = json?.summary;
    const exact =
      !!worstBin &&
      !!summaryWorst &&
      summaryWorst.worstBinIndex === worstBin.i &&
      summaryWorst.worstStationDisplayM !== null;
    const worstStationDisplayM = !worstBin
      ? null
      : exact
        ? summaryWorst!.worstStationDisplayM!
        : (worstBin.staStartDisplayM + worstBin.staEndDisplayM) / 2;
    return {
      violations,
      worst: worstBin?.maxIntrusionM ?? null,
      worstStationDisplayM,
      worstStationExact: exact,
      worstStationRange: worstBin ? formatStationRange(worstBin, 2) : null,
      binsWithViolations: scoped.filter((b) => b.violationCount > 0).length,
      binCount: scoped.length,
      // The Details scope sentence. INTERNAL chainage for the length: a
      // station equation makes display stations jump, and the length swept is
      // what the bins actually cover along the line.
      lengthM: scoped.reduce((acc, b) => acc + (b.staEndInternalM - b.staStartInternalM), 0),
      scored: scoped.reduce((acc, b) => acc + b.pointCount, 0),
    };
  }, [scoped, bins.length, json, meta]);

  // Finding I3, mirroring `StationVolumeDock`: every station this dock prints
  // was computed against the alignment as it stood when the run went out. If
  // that alignment has been edited since, the figures are still correct about
  // the cloud and wrong about WHERE — which is the failure a reader cannot see.
  // An unregistered alignment matches by definition (`alignmentHashMatches`),
  // so a project that has not loaded the LandXML yet gets no false alarm.
  const liveAlignment: Alignment | undefined = meta ? alignments.get(meta.alignment.key) : undefined;
  const alignmentStale = meta ? !alignmentHashMatches(meta.alignment, liveAlignment) : false;

  // ---- Opening the dock starts a walk (Task 6a) ---------------------------
  //
  // That is what makes the chart cursor, the 3D plates and the envelope one
  // instrument rather than three — and it is what makes `[` / `]` work, since
  // `AlignmentWalkToolbar` keys off `stationDockOpenAtom`, which does not care
  // which dataset is open. `analysis.clearance` and `viewer.alignmentWalk` are
  // the same tier, so this is not a way around the walk gate; on a lower tier
  // the dock simply never starts a walk.
  const isClearanceDock = isClearance && !!dock;
  useEffect(() => {
    if (!gate.allowed || !isClearanceDock || !liveAlignment || !json) return;
    // A card's "Go to worst" that opened this dock: start AT the target
    // rather than at the run's first station and then jump (`walkTarget`
    // below consumes it in the same commit).
    const pending = store.get(clearanceWalkTargetAtom);
    const seed = pending && pending.layerId === dock?.layerId ? pending.station : json.params.stationFromInternalM;
    setWalk((w) =>
      w?.key === liveAlignment.key
        ? w
        : {
            key: liveAlignment.key,
            station: seed,
            step: json.params.stationIntervalM || DEFAULT_WALK_STEP,
            snapKeyPoints: false,
            follow: false,
          },
    );
  }, [gate.allowed, isClearanceDock, liveAlignment, json, setWalk, store, dock?.layerId]);

  // ---- "Clip to station" (Task 6d) ---------------------------------------
  //
  // A line-for-line port of `StationVolumeDock`'s own take-over, with one
  // difference that is forced on it: the slab's dimensions come from the
  // PROFILE's bounding box (`clearanceClipDims`), because a clearance run
  // writes no `station_offsets.f32` planes for `clipDimsForRun` to read
  // (ruling R1).
  const walkActive = !!walk && !!meta && walk.key === meta.alignment.key;
  const clipAllowed = gate.allowed && clipGate.allowed;
  // Recomputed as the walk steps, and deliberately: `verticalCentre` is an
  // ABSOLUTE elevation (ruling P7), so on a graded alignment the slab has to
  // rise with the station or it slides out of the cloud a few hundred metres
  // along. The station is the only moving part — the memo is stable between
  // steps, so the effect below does not re-fire per frame.
  const walkStation = walk?.station;

  /**
   * Where the reader "stands" when no walk can: a run whose alignment is not
   * loaded in this project has no `alignmentWalkAtom` to move, but stepping
   * the section and "Go to worst" still have to go somewhere. Keyed by layer
   * so it never follows the reader to another run.
   */
  const [idleCursor, setIdleCursor] = useState<{ layerId: string; station: number } | null>(null);
  const cursorStation: number | null =
    walkActive && walkStation != null
      ? walkStation
      : idleCursor && idleCursor.layerId === dock?.layerId
        ? idleCursor.station
        : null;

  /**
   * Which station the cross-section stands at. The WALK wins — that is what
   * makes stepping through the corridor and watching the section change feel
   * like one instrument rather than two — then the brush (a reader who
   * dragged a range on the Chart finds its first interval drawn), then the
   * run's WORST interval: the one section a reader opening the tab cold
   * wants, where bin 0 was usually a clean stretch of nothing.
   *
   * The walk lookup is `binAtStation`, the table's own rule (half-open, last
   * interval's end inclusive), so a walk parked on the corridor's very last
   * station still draws the last section rather than falling through.
   *
   * Array POSITION, not `bin.i`: the section grid's station axis is indexed by
   * position in `bins[]` (the contract the writer and this reader share), and
   * the two coincide today only because the worker emits every bin in order.
   */
  const worstBinIndex = json?.summary.worstBinIndex ?? null;
  const sectionBinIndex = useMemo(() => {
    if (cursorStation != null) {
      const hit = binAtStation(bins, cursorStation);
      const i = hit == null ? -1 : bins.findIndex((b) => b.i === hit);
      if (i >= 0) return i;
    }
    if (selEff) {
      const i = bins.findIndex((b) => b.i >= selEff.fromBin && b.i <= selEff.toBin);
      if (i >= 0) return i;
    }
    if (worstBinIndex != null) {
      const i = bins.findIndex((b) => b.i === worstBinIndex);
      if (i >= 0) return i;
    }
    return 0;
  }, [bins, cursorStation, selEff, worstBinIndex]);

  /**
   * Click walks, drag brushes (the station-volumes contract): every one-shot
   * navigation — a section step, "Go to worst", previous / next violation —
   * moves the WALK and clears any brush. Setting a one-interval brush instead
   * narrowed every KPI to that interval and hid the run's verdict, which is
   * not what "show me the worst point" asked for.
   */
  const dockLayerId = dock?.layerId;
  const alignmentKey = meta?.alignment.key;
  const moveCursor = useCallback(
    (station: number) => {
      if (!dockLayerId) return;
      setSelection((s) => (s && s.layerId === dockLayerId ? null : s));
      if (walkActive) setWalk((w) => (w && w.key === alignmentKey ? { ...w, station } : w));
      else setIdleCursor({ layerId: dockLayerId, station });
    },
    [alignmentKey, dockLayerId, setSelection, setWalk, walkActive],
  );

  /** Step the section one interval, taking the walk with it. */
  const stepSectionBin = useCallback(
    (next: number) => {
      const b = bins[Math.max(0, Math.min(bins.length - 1, next))];
      if (b) moveCursor(binCentreStation(b));
    },
    [bins, moveCursor],
  );

  /** The violating intervals either side of where the reader stands
   *  (`adjacentViolationBin`) — what the chevrons and `Shift+[` / `Shift+]`
   *  walk to, and why either is inert when it is null. */
  const prevViolation = useMemo(() => adjacentViolationBin(bins, cursorStation, -1), [bins, cursorStation]);
  const nextViolation = useMemo(() => adjacentViolationBin(bins, cursorStation, 1), [bins, cursorStation]);
  const stepViolation = useCallback(
    (dir: ViolationStepDir) => {
      const target = dir === 1 ? nextViolation : prevViolation;
      if (target) moveCursor(binCentreStation(target));
    },
    [moveCursor, nextViolation, prevViolation],
  );

  // The hotkeys' half of previous / next violation: `useViewerHotkeys` only
  // ASKS (it has no bins); the request is consumed here and reset, so a dock
  // that mounts later never replays a keypress it did not see.
  //
  // NOT gated (round-3 decision 4, review finding 6): navigation is reading
  // results the reader already has, so the ◀ ▶ chevrons, "Go to worst" and
  // the section stepper all work wherever this dock does — and the keys
  // printed in their own tooltips ("Shift+[") must do the same thing, not
  // silently nothing on a locked tier. The paid parts (the 3D envelope, the
  // point-cloud take-over, the clip slab, the walk itself) stay gated.
  const [violationStepRequest, setViolationStepRequest] = useAtom(clearanceViolationStepAtom);
  useEffect(() => {
    if (!violationStepRequest) return;
    setViolationStepRequest(null);
    if (!isClearance) return;
    stepViolation(violationStepRequest.dir);
  }, [violationStepRequest, setViolationStepRequest, isClearance, stepViolation]);

  /**
   * The card's "Go to worst" (`clearanceWalkTargetAtom`), answered once the
   * run is in hand. With a live alignment and the gate open it is a WALK —
   * the existing one moved, or a new one on this alignment when there is none
   * or it follows another line (the card's button is a request to look at
   * THIS run's worst point). Otherwise the stand-in cursor. Either way the
   * brush goes, as for every click-walk (`moveCursor`). Declared after the
   * walk-start effect so a fresh walk seeded from the target is not then
   * overwritten with the run's first station.
   *
   * Review finding 2: consumed once the fetch has SETTLED, not once the JSON
   * is in hand. Waiting for `json` left a target pending forever on a run
   * whose fetch failed or that has no JSON — and it replayed the next time
   * that run was opened, landing a plain "Open results" on the worst station.
   * The step comes from the layer metadata, which every open dock has.
   */
  const [walkTarget, setWalkTarget] = useAtom(clearanceWalkTargetAtom);
  const runStep = json?.params.stationIntervalM ?? meta?.params.stationIntervalM;
  useEffect(() => {
    if (
      !walkTarget ||
      !isClearance ||
      !dockLayerId ||
      walkTarget.layerId !== dockLayerId ||
      runStep === undefined ||
      isLoading
    )
      return;
    setWalkTarget(null);
    const { station } = walkTarget;
    setSelection((s) => (s && s.layerId === dockLayerId ? null : s));
    if (gate.allowed && liveAlignment) {
      const key = liveAlignment.key;
      setWalk((w) =>
        w?.key === key
          ? { ...w, station }
          : { key, station, step: runStep || DEFAULT_WALK_STEP, snapKeyPoints: false, follow: false },
      );
    } else setIdleCursor({ layerId: dockLayerId, station });
  }, [
    walkTarget,
    setWalkTarget,
    isClearance,
    dockLayerId,
    runStep,
    isLoading,
    gate.allowed,
    liveAlignment,
    setSelection,
    setWalk,
  ]);

  /**
   * "Jump to station" — `StationVolumeDock`'s camera follow, the same
   * mechanism rather than a fork: on every walked-station change, emit the
   * `lookAt` pose "Look at section" uses (`lookAtSectionPose`), framed on this
   * run's profile box (`clearanceClipDims`, the slab "Clip to station" cuts).
   * Ticking it jumps at once. Its own flag, not the walk's `follow`, for the
   * reason the sibling dock gives: `follow` moves the camera along the line
   * and is a different instrument.
   *
   * The run's params are read at EMIT time through a ref: the layer metadata
   * object changes identity on every layers refetch, and depending on it would
   * re-jump a camera the user has since moved away.
   */
  const [jumpToStation, setJumpToStation] = useState(false);
  const toggleJumpToStation = () => {
    if (!walkActive) return;
    setJumpToStation((on) => !on);
  };
  // The follow is per dock-opening, not per component: this component never
  // unmounts, and a dock closed by anything but its own close button (the
  // card's toggle, a volumes card taking the slot) must not reopen with the
  // camera already jumping. Reset whenever the open clearance run changes.
  const openClearanceLayer = isClearance ? (dock?.layerId ?? null) : null;
  useEffect(() => {
    setJumpToStation(false);
    // The metric choice is per run too: the next run opens on its own default.
    setMetricChoice(null);
    // Review finding 2(b): a card's "Go to worst" posted for another run (or
    // for this one before a close) must not outlive the slot moving on — it
    // would fire the next time that run is opened. A target for the run that
    // is open NOW is the one the card just posted alongside opening it.
    // (A grip drag in flight is `DockFrame`'s, via `dragResetKey`.)
    const pending = store.get(clearanceWalkTargetAtom);
    if (pending && pending.layerId !== openClearanceLayer) store.set(clearanceWalkTargetAtom, null);
  }, [openClearanceLayer, store]);
  const followParamsRef = useRef(meta?.params ?? null);
  useEffect(() => {
    followParamsRef.current = meta?.params ?? null;
  }, [meta]);
  useEffect(() => {
    if (!jumpToStation || !walkActive || walkStation === undefined || !alignmentKey) return;
    const alignment = store.get(alignmentsAtom).get(alignmentKey);
    const params = followParamsRef.current;
    if (!alignment || !params) return;
    const dims = clearanceClipDims(params, alignment, walkStation);
    viewerEvents.emit('lookAt', lookAtSectionPose(alignment, walkStation, dims.width, dims.height));
  }, [jumpToStation, walkActive, walkStation, alignmentKey, store]);

  const clipDims = useMemo(
    () =>
      meta && liveAlignment && walkStation !== undefined
        ? clearanceClipDims(meta.params, liveAlignment, walkStation)
        : null,
    [meta, liveAlignment, walkStation],
  );

  /**
   * "Look at cross-section" — the station dock's button and `L` key, the same
   * mechanism (`lookAtSectionPose`), so the two docks' Cross-section tabs
   * carry the same camera action in the same place. Framed on this run's
   * profile box (`clipDims`), it needs a live walk on a loaded alignment: a
   * station to stand at and a tangent to stand perpendicular to. Camera
   * follow goes on with it, as in the sibling, so stepping the section keeps
   * the slice in front of the reader instead of leaving them where it was.
   */
  const lookAtSection = () => {
    if (!walkActive || !liveAlignment || walkStation === undefined || !clipDims) return;
    viewerEvents.emit('lookAt', lookAtSectionPose(liveAlignment, walkStation, clipDims.width, clipDims.height));
    setWalk((w) => (w && !w.follow ? { ...w, follow: true } : w));
  };

  /**
   * What the walk's clip was before we took it over, and what the viewer's
   * clip box looked like. Refs, not state: they are written from an effect and
   * never rendered, and React Compiler forbids reading a ref during render.
   *
   * `undefined` on the walk ref means "we have not taken it over"; `null` is a
   * real previous value (no slice), which is why the two cannot share a
   * sentinel.
   */
  const clipRestoreRef = useRef<ClipDims | null | undefined>(undefined);
  // GEOMETRY is part of "as we found it", not just the flags. Restoring
  // `enabled`/`clipMode` alone handed a user who already had a clip box of
  // their own back a box that was still enabled and still sitting on the
  // station slab this dock had moved it to — which reads, correctly, as
  // "I turned Clip to station off and the clipping did not turn off".
  const boxRestoreRef = useRef<Pick<
    ClipBoxState,
    'enabled' | 'interactionMode' | 'clipMode' | 'center' | 'halfSize'
  > | null>(null);

  /** Give the walk and the clip box back exactly as we found them. */
  const releaseClip = useCallback(() => {
    if (clipRestoreRef.current !== undefined) {
      const previous = clipRestoreRef.current;
      clipRestoreRef.current = undefined;
      setWalk((w) => (w ? { ...w, clip: previous } : w));
    }
    const box = boxRestoreRef.current;
    if (box) {
      boxRestoreRef.current = null;
      store.set(clipBoxStateAtom(projectId), (prev) => ({ ...prev, ...box }));
    }
  }, [projectId, setWalk, store]);

  useEffect(() => {
    if (!clipToBin || !clipDims || !meta || !walkActive || !liveAlignment || !clipAllowed) {
      // Restore exactly once, and only if we were the ones who changed it.
      releaseClip();
      return;
    }
    const current = store.get(alignmentWalkAtom);
    if (!current) return;
    // The slab belongs to a BIN, not to wherever the slider happens to sit, so
    // the station is snapped ON THE WAY ON and never again: re-snapping on
    // every `clipDims` identity change would yank a mid-bin walk out from
    // under the user for no reason they could see.
    const takingOver = clipRestoreRef.current === undefined;
    if (takingOver) clipRestoreRef.current = current.clip ?? null;
    const i = binIndexForStation(meta.params, bins.length, current.station);
    const station = takingOver && bins[i] ? binCentreStation(bins[i]) : current.station;
    setWalk({ ...current, station, clip: clipDims });
    // Enable the box in the SAME batch as the walk write, exactly as the walk
    // toolbar's own clip-follow does: that toolbar cancels a `walk.clip` it
    // sees alongside a switched-off box (it reads that as "the user turned the
    // box off elsewhere"), so writing the walk alone would be undone before
    // the next frame.
    const box = store.get(clipBoxStateAtom(projectId));
    if (!boxRestoreRef.current) {
      boxRestoreRef.current = {
        enabled: box.enabled,
        interactionMode: box.interactionMode,
        clipMode: box.clipMode,
        center: box.center,
        halfSize: box.halfSize,
      };
    }
    store.set(clipBoxStateAtom(projectId), {
      ...box,
      ...clipBoxAtStation(liveAlignment, station, clipDims),
      enabled: true,
      interactionMode: 'VIEW',
      clipMode: 'CLIP_OUTSIDE',
    });
  }, [clipToBin, clipDims, meta, walkActive, liveAlignment, clipAllowed, bins, projectId, releaseClip, setWalk, store]);

  /**
   * `walk.clip` has two owners — this toggle and the walk toolbar's own ⬚
   * button, which also nulls it when the clip box is switched off anywhere
   * else. Nothing tells us when the other one acts, and none of the effect
   * above's dependencies move when it does, so the checkbox would sit there
   * ticked over a slab that has stopped following the station.
   *
   * Follow the real state, one way: the slab is gone, so stop claiming it. The
   * walk-clip restore is dropped with it — the other owner has already decided
   * what `walk.clip` should be — but `boxRestoreRef` is deliberately kept, so
   * the release below still hands the viewer's own clip box back.
   */
  const walkClipPresent = !!walk?.clip;
  useEffect(() => {
    if (!clipToBin || !walkActive) return;
    if (clipRestoreRef.current === undefined) return;
    // The LIVE value, not `walkClipPresent`: on the commit that ticks the box
    // both effects run against a render where `walk.clip` was still null, and
    // the render-time snapshot would read the take-over as an external clear
    // and undo it. `walkClipPresent` stays in the dependency list as the
    // trigger — it is what changes when another owner acts.
    if (store.get(alignmentWalkAtom)?.clip) return;
    clipRestoreRef.current = undefined;
    setClipToBin(false);
  }, [clipToBin, walkActive, walkClipPresent, setClipToBin, store]);

  // Closing the dock is not "keep my cloud sliced": the toggle is a dock
  // control, so the take-over ends with the dock. `releaseClip` is stable, so
  // this cleanup only ever runs on unmount.
  useEffect(() => releaseClip, [releaseClip]);

  /**
   * The mirror of `StationVolumeDock`'s own stale-latch reset (its finding
   * F-24): a volumes chip REPLACES the shared `dock` atom rather than calling
   * this dock's close handler, so the reset in that handler never runs and
   * `stationClipToBinAtom` for the clearance layer stays latched `true` on its
   * own atomFamily entry. Track the layerId THIS dock last owned — independent
   * of `dock` moving on to another dataset — so the reset lands on that entry.
   */
  const ownLayerIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isClearanceDock && dock) ownLayerIdRef.current = dock.layerId;
  }, [isClearanceDock, dock]);
  useEffect(() => {
    if (isClearanceDock) return;
    const staleLayerId = ownLayerIdRef.current;
    if (!staleLayerId) return;
    ownLayerIdRef.current = null;
    store.set(stationClipToBinAtom(staleLayerId), false);
  }, [isClearanceDock, store]);

  /** Where "Go to worst" goes, or null on a clean run (`clearanceWorstTarget`). */
  const worstTarget = useMemo(() => (json ? clearanceWorstTarget(json) : null), [json]);

  /**
   * The run's verdict, rows and rule — `useClearanceVerdict`, the ONE builder
   * the overlay card's chip uses too, so the two chips cannot list different
   * tests or quote different bands. SUMMARY-level, from the run's own counts:
   * the layer metadata carries the same summary the JSON does, so the verdict
   * is on screen before the byproduct has been fetched. Called above the early
   * return (it is a hook), hence the empty stand-in with no dock open.
   */
  const verdictView = useClearanceVerdict(
    json?.summary ?? meta?.summary ?? EMPTY_VERDICT_SUMMARY,
    json?.params.nearMissBandsM ?? meta?.params.nearMissBandsM,
  );


  // Opening the dock moves focus into it — the sibling dock's rule: Escape has
  // to work on the FIRST press, and a screen-reader user has to land inside the
  // thing that just appeared. `DockFrame` focuses ITSELF whenever this key
  // turns non-null (not the active tab: a programmatic focus on a tab painted
  // a `focus-visible` ring on every open, audit A4). Keyed on `meta` as well
  // as the layer, because the frame only renders once the layer's metadata is
  // in hand, which can be a render after the dock atom was set.
  const openLayerId = isClearance ? (dock?.layerId ?? null) : null;
  const focusKey = openLayerId && meta ? openLayerId : null;

  /**
   * The re-run, for the two states only a new run can fix: a byproduct from a
   * newer worker (schema too new) and a run with no results file — and, for
   * any run, the ⋯ menu. The run dialog opens through the launcher's own
   * opener (`openClearance`), seeded with the point cloud this run was scored
   * on — the same entry the layer menu and the Analyses hub use, so the shell
   * class and candidate pool cannot drift. Re-running is running: the gate is
   * checked at click time.
   */
  const { openDialog } = useDialog();
  const rerunSource = meta ? layers.find((l) => l.id === meta.pointcloudLayerId) : undefined;
  const openRerun = () => {
    if (!gate.allowed) {
      gate.promptUpgrade();
      return;
    }
    if (!rerunSource) return;
    openClearance({ openDialog, projectId, layers }, rerunSource);
  };
  /** The re-run button, or the sentence that explains its absence — a control
   *  that opens nothing reads as a bug; the reason does not. Neither on a
   *  shared link, which can never re-run. */
  const rerunControl = isSharedLink ? null : rerunSource ? (
    <Button data-testid='clearance-rerun' type='button' size='sm' variant='outline' className='h-7' onClick={openRerun}>
      {t('clearance.dock.rerun')}
      {!gate.allowed && <UpgradeBadge gate={gate.gate} />}
    </Button>
  ) : (
    <p data-testid='clearance-rerun-unavailable' className='text-slate-500 dark:text-stone-400'>
      {t('stationVolumes.empty.rerunUnavailable')}
    </p>
  );

  /**
   * The grip's new height, written onto the dock as it was when the drag
   * started (`DockFrame` keeps the callback it had at pointerdown — the old
   * `base` snapshot). A drag cannot outlive this run's slot: the frame aborts
   * it on `dragResetKey` and on unmount, so this never reopens a closed dock.
   */
  const setHeight = (height: number) => {
    if (dock) setDock({ ...dock, height });
  };

  const close = () => {
    setDock(null);
    setSelection(null);
    // The slab takes over the viewer's clip box — shared state — so reopening
    // the dock has to ask again rather than silently re-slicing the cloud
    // (ruling D36). The envelope stays sticky because it touches nothing shared.
    setClipToBin(false);
    // The camera follow is a dock control too: a reader reopening the dock
    // must not find the camera jumping before they asked again.
    setJumpToStation(false);
    // Review finding 2(a): a card's "Go to worst" still pending (its run's
    // JSON never arrived) dies with the dock rather than replaying on the next
    // plain "Open results".
    setWalkTarget(null);
    // A grip drag in flight needs nothing here: the frame unmounts with the
    // dock and aborts its listeners on the way out.
  };

  // Escape peels one layer at a time, as in `StationVolumeDock`: the brush
  // first (it is the thing the reader most likely wants back), then the dock.
  // `DockFrame` only hands over keys pressed INSIDE its own DOM — an Escape
  // that closes a portalled menu must not also close the dock under it.
  // `selEff`: a stale brush has nothing on screen to peel, so the first
  // Escape must not be spent on it.
  const onEscape = () => {
    if (selEff) {
      setSelection(null);
      return;
    }
    close();
  };

  /**
   * "Copy as TSV" — the bins in view (the brush when there is one), in the
   * worker CSV's own columns (`clearanceBinsToTsv`). Same failure handling
   * and the same toasts as the sibling dock's `copyTsv`: clipboard access can
   * be refused, absent, or throw synchronously, and an empty paste is the one
   * outcome the reader must not have to discover for themselves. The success
   * toast is this dock's own PLURAL key (review finding 4) — the shared
   * `stationVolumes.export.copied` printed "1 station intervals".
   */
  const copyTsv = () => {
    if (!json) return;
    const tsv = clearanceBinsToTsv(scoped);
    const copiedBins = tsv.split('\n').length - 1;
    try {
      const written = navigator.clipboard?.writeText(tsv);
      if (!written) {
        toast.error(t('stationVolumes.export.copyFailed'));
        return;
      }
      written
        .then(() =>
          toast.success(t('clearance.dock.copied', { count: copiedBins, formatted: copiedBins.toLocaleString() })),
        )
        .catch(() => toast.error(t('stationVolumes.export.copyFailed')));
    } catch {
      toast.error(t('stationVolumes.export.copyFailed'));
    }
  };

  if (!dock || !isClearance || !meta) return null;

  const p = meta.params;
  /** What the loading/error badge is ABOUT. The derived layer's own name when
   *  the layers query has it, otherwise the alignment the run swept — the same
   *  fallback the sibling dock's badge uses. */
  const badgeName = layer?.name ?? meta.alignment.name;
  const bands = (json?.params.nearMissBandsM ?? p.nearMissBandsM ?? []).map((b) => formatFixed(b, 2)).join(' / ');
  // Finding F-6: every station this dock prints — the KPI row, the brush chip,
  // the chart/table axes, and the worker's own CSV — is a DISPLAY station. The
  // header's range must be one too. Prefer the bins' own display stations
  // (matches the CSV exactly); fall back to converting the run's INTERNAL
  // params through the alignment when bins have not loaded yet — the same
  // `rangeText` shape `StationVolumeDock` builds, at this dock's 2 dp.
  const rangeText = formatStationRange(
    {
      staStartDisplayM: bins.length
        ? bins[0].staStartDisplayM
        : internalToDisplayRaw(p.stationFromInternalM, meta.alignment.staStart, meta.alignment.equations),
      staEndDisplayM: bins.length
        ? bins[bins.length - 1].staEndDisplayM
        : internalToDisplayRaw(p.stationToInternalM, meta.alignment.staStart, meta.alignment.equations),
    },
    2,
  );
  /** The alignment's LAYER name when the run recorded one — the overlay
   *  card's own expression, so the card and the dock name the same line the
   *  same way. */
  const alignmentName = meta.alignment.layerName ?? meta.alignment.name;

  const summary = json?.summary ?? meta.summary;
  /** The outermost band — what "nothing came near" is measured against, and
   *  the band the verdict turns MARGINAL on (`useClearanceVerdict`). */
  const outerBand = verdictView.band;

  /**
   * A clean run whose chart would draw NOTHING — no violation and no point in
   * any near-miss band, so every bar of every metric is zero. Only then does
   * the body say it in words (spec §B5); a MARGINAL run opens on its near-miss
   * bars instead, which are the chart worth reading.
   */
  const cleanAndEmpty =
    !!json &&
    json.summary.violationCount === 0 &&
    json.summary.nearMiss.within0_35M === 0 &&
    json.summary.scoredPointCount > 0;
  /** What the bars plot: the reader's choice, else the run's default. */
  const metric: ClearanceMetric = metricChoice ?? defaultClearanceMetric(summary);
  /** The PASS body until the reader asks for a chart anyway (the empty state's
   *  own "Show near-miss chart", or any segment of the metric control). */
  const showEmptyState = cleanAndEmpty && metricChoice === null;

  /** What the KPI row is scoped to — remounts the figures (the 90 ms
   *  cross-fade) when a brush takes over or lets go, as in the sibling dock. */
  const kpiScope = selEff ? 'selection' : 'corridor';
  /** `totals` already falls back to the run summary before the bins land
   *  (finding 1) — never a 0 that reads as "clean" for a run that is not. */
  const kpiViolations = totals.violations;
  /** A clean run with no brush: the KPI row says how close it came instead of
   *  printing three dashes (worst / at station / affected intervals). */
  const cleanRun = !selEff && kpiViolations === 0;
  /** The interval the cross-section (and the Profile fallback sketch) shows. */
  const sectionBin = bins[sectionBinIndex] ?? null;

  /** Below 520 px the chart has no room (the effect above has already swapped
   *  it for the table); the tab says why rather than silently bouncing. */
  const chartTooNarrow = width < DOCK_VERY_NARROW_PX;

  // `selEff` already drops a brush with no bins under it (see its comment).
  const selActive = !!selEff;
  const selectionText = selActive
    ? formatStationRange(
        { staStartDisplayM: scoped[0].staStartDisplayM, staEndDisplayM: scoped[scoped.length - 1].staEndDisplayM },
        2,
      )
    : '';

  // ---- the PDF reports (clearance report spec §5.1 / §5.3) ------------------
  //
  // Both go through `onOpenPdfReport` — `useOpenAnalysisReport`, which owns the
  // `report.pdf` paywall. The items' `disabled` below is a DATA guard only.
  const hasResultsFile = !!meta.byproducts.json;
  const hasSections = !!meta.sectionBins && !!meta.byproducts.section;
  /** The summary is always the WHOLE run: the brush windows only the
   *  per-station document. The server reads the results file itself, so it
   *  does not wait for `json`. */
  const openSummaryPdf = () => {
    if (!hasResultsFile) return;
    onOpenPdfReport?.({ projectId, layerId: dock.layerId, layerName: badgeName, mode: 'clearance', alignmentStale });
  };
  const openPerStationPdf = () => {
    // The second half of the item's data guard, for the keyboard — Radix
    // `disabled` is an attribute, not an HTML one.
    if (!json || !hasResultsFile || !hasSections) return;
    // The brush if there is one, the whole run otherwise — the same whole-bin
    // window and pageable/flagged rule the server applies (`@gis/clearance`).
    const scope = clearancePerStationScope(
      json.bins,
      selEff ? { fromBin: selEff.fromBin, toBin: selEff.toBin } : null,
      verdictView.verdict,
    );
    if (scope.block === 'empty') {
      toast.error(t('clearance.export.perStationPdfEmpty'));
      return;
    }
    if (scope.block === 'tooMany') {
      toast.error(
        scope.flaggedCount === 0
          ? t('clearance.export.perStationPdfTooManyAll', { count: scope.allCount, max: MAX_CLEARANCE_PAGES })
          : t('clearance.export.perStationPdfTooMany', { max: MAX_CLEARANCE_PAGES }),
      );
      return;
    }
    onOpenPdfReport?.({
      projectId,
      layerId: dock.layerId,
      layerName: badgeName,
      mode: 'clearancePages',
      stationFrom: scope.stationFrom,
      stationTo: scope.stationTo,
      // The WINDOW in display stations, at the brush chip's 2 dp.
      stationRangeLabel:
        scope.displayFrom !== null && scope.displayTo !== null
          ? formatStationRange({ staStartDisplayM: scope.displayFrom, staEndDisplayM: scope.displayTo }, 2)
          : null,
      allCount: scope.allCount,
      flaggedCount: scope.flaggedCount,
      defaultFilter: scope.defaultFilter,
      alignmentStale,
    });
  };

  // ---- the KPI strip --------------------------------------------------------
  //
  // Verdict first and unlabelled (the chip is its own label), then the
  // figures. Drop order on a narrow dock (spec §B1): Affected intervals, then
  // At station — they move into Details, they are never wrapped onto a row.
  const verdictItem: DockKpiItem = {
    key: 'verdict',
    priority: 0,
    node: (
      // Hidden (never recomputed) while a brush is active, as the sibling dock
      // treats its corridor verdict: a corridor-wide answer beside brushed
      // figures would be attributed to a sub-range it was never measured over.
      <div data-testid='clearance-verdict' className='flex items-center'>
        <VerdictChip
          verdict={verdictView.verdict}
          rows={verdictView.rows}
          footnote={verdictView.rule}
          title={verdictView.rule}
        />
      </div>
    ),
  };
  const violationsItem: DockKpiItem = {
    key: 'violations',
    priority: 0,
    node: (
      <DockKpi
        scopeKey={kpiScope}
        label={t('clearance.kpi.violations')}
        value={kpiViolations.toLocaleString()}
        testId='clearance-kpi-violations'
      />
    ),
  };
  const figureItems: DockKpiItem[] = cleanRun
    ? [
        {
          // A clean run has no worst intrusion, no station for it and no
          // affected interval — three em dashes that say nothing. What a clean
          // run CAN say is how close it came.
          key: 'margin',
          priority: 0,
          node: (
            <DockKpi
              scopeKey={kpiScope}
              label={t('clearance.kpi.tightestMargin')}
              title={t('clearance.kpi.tightestMarginHelp')}
              value={summary.minMarginM == null ? '—' : formatFixed(summary.minMarginM, 3)}
              unit='m'
              unitTestId='clearance-kpi-margin-unit'
              testId='clearance-kpi-margin'
            />
          ),
        },
        // MARGINAL (spec §B5): how many points sit in the band that made it so.
        ...(verdictView.verdict === 'MARGINAL'
          ? [
              {
                key: 'nearMisses',
                priority: 1 as const,
                node: (
                  <DockKpi
                    scopeKey={kpiScope}
                    label={t('clearance.kpi.nearMisses', { band: outerBand })}
                    value={summary.nearMiss.within0_35M.toLocaleString()}
                    testId='clearance-kpi-near-misses'
                    color='var(--sv-caution)'
                  />
                ),
              },
            ]
          : []),
      ]
    : [
        {
          key: 'worst',
          priority: 0,
          node: (
            <DockKpi
              scopeKey={kpiScope}
              label={t('clearance.kpi.worst')}
              value={totals.worst === null ? '—' : formatFixed(totals.worst, 3)}
              unit='m'
              unitTestId='clearance-kpi-worst-unit'
              testId='clearance-kpi-worst'
              // The violation ink, from the same map the bars and the envelope
              // are painted with: an intrusion is the red figure.
              color={totals.worst !== null && totals.worst > 0 ? CLEARANCE_FILL.violation : undefined}
            />
          ),
        },
        {
          // `staOnly`, not `staTag`: the label already says "At station", and
          // "STA" in the value too made the tile wrap (audit A7). No leading
          // `~` either — an inexact figure carries the bin's span in its title.
          key: 'station',
          priority: 2,
          node: (
            <DockKpi
              mono
              scopeKey={kpiScope}
              label={t('clearance.kpi.worstStation')}
              value={totals.worstStationDisplayM === null ? '—' : staOnly(totals.worstStationDisplayM, 2)}
              title={
                totals.worstStationDisplayM === null || totals.worstStationExact
                  ? undefined
                  : t('clearance.kpi.worstStationApprox', { range: totals.worstStationRange ?? '' })
              }
              testId='clearance-kpi-worst-station'
            />
          ),
        },
        {
          key: 'bins',
          priority: 1,
          node: (
            <DockKpi
              scopeKey={kpiScope}
              label={t('clearance.kpi.bins')}
              value={totals.binsWithViolations.toLocaleString()}
              testId='clearance-kpi-bins'
            />
          ),
        },
      ];
  const kpiItems: DockKpiItem[] = [...(selActive ? [] : [verdictItem]), violationsItem, ...figureItems];

  // ---- ⓘ Details: the reference material the header and footer used to print
  //
  // One scope per sentence — mixing a selection's interval count with the
  // corridor's length is how a reader mis-quotes. Each sentence carries ONE
  // pluralised count (`count` + `formatted`, finding 4); the points scored
  // get a row of their own rather than a second plural in the same sentence.
  const scopeLengthM = totals.lengthM ?? p.stationToInternalM - p.stationFromInternalM;
  const scopeSentence = selActive
    ? t('clearance.dock.scopeSelection', {
        count: bins.length,
        formatted: totals.binCount.toLocaleString(),
        total: bins.length.toLocaleString(),
        length: Math.round(scopeLengthM).toLocaleString(),
      })
    : t('clearance.dock.scopeCorridor', {
        count: totals.binCount,
        formatted: totals.binCount.toLocaleString(),
        // B-2: intervals may be fractional — 2 dp with trailing zeros
        // trimmed, so 20 prints "20" and 12.5 prints "12.5".
        interval: String(Math.round(p.stationIntervalM * 100) / 100),
        length: Math.round(scopeLengthM).toLocaleString(),
      });
  const detailsSections: DockDetailsSection[] = [
    {
      key: 'run',
      title: t('clearance.dock.detailsRun'),
      rows: [
        { key: 'alignment', label: t('clearance.dock.detailsAlignment'), value: alignmentName },
        { key: 'range', label: t('stationVolumes.panel.rangeLabel'), value: rangeText, mono: true },
        { key: 'scope', value: scopeSentence, testId: 'clearance-footer-scope' },
        {
          key: 'scored',
          label: t('clearance.dock.verdictScored'),
          value: totals.scored.toLocaleString(),
          testId: 'clearance-details-scored',
        },
        {
          key: 'method',
          testId: 'clearance-footnote',
          value: (
            <span title={t('clearance.dock.footnoteHelp', { violationClass: p.violationClass })}>
              {t('clearance.dock.footnote', {
                violationClass: p.violationClass,
                // Signed: the profile hangs BELOW the alignment at a negative
                // offset, and "2.00" for "-2.00" would hang it the wrong way up.
                offset: formatSigned(p.verticalOffsetM, 2),
              })}
            </span>
          ),
        },
      ],
    },
    {
      key: 'params',
      title: t('clearance.dock.detailsParams'),
      rows: [
        {
          key: 'bands',
          testId: 'clearance-chip-bands',
          value: (
            <span className='sv-num' title={t('clearance.dock.chipBandsHelp')}>
              {t('clearance.dock.chipBands', { bands: bands || '—', interpolation: { escapeValue: false } })}
            </span>
          ),
        },
        // The sibling dock's sentence, from the sibling dock's own key: both
        // docks print signed offsets in the same station frame, so they must
        // state the convention in the same words.
        { key: 'offset', testId: 'clearance-chip-offset', value: t('stationVolumes.panel.offsetConvention') },
      ],
    },
    {
      // Ruling K4: the standard the dimensions came from travels with the
      // run, so the result can always be traced back to it. In full here — a
      // citation truncated to "Dimensions per Transport Canada E-…" is the one
      // string that has to be quotable.
      key: 'source',
      title: t('clearance.dock.detailsSource'),
      rows: [
        { key: 'profile', label: t('clearance.dock.detailsProfile'), value: meta.profile.name },
        ...(meta.profile.sourceAttribution
          ? [{ key: 'attribution', value: meta.profile.sourceAttribution, testId: 'clearance-dock-attribution' }]
          : []),
      ],
    },
  ];

  // ---- failures, in the reader's language (finding 14) ----------------------
  const loadError = error && !schemaTooNew ? describeLoadError(error) : null;
  const loadErrorText = schemaTooNew
    ? t('clearance.dock.schemaTooNew')
    : loadError
      ? t(loadError.key, { status: loadError.status ?? '' })
      : '';
  const csvError = csv.error ? describeLoadError(csv.error) : null;

  /** The point-cloud colour radios' reason a mode is out of reach, if any. */
  const viewModeReason = viewModeUnavailable ? t(VIEW_MODE_UNAVAILABLE_KEYS[viewModeUnavailable]) : null;
  /** The Walk switches' shared data condition. */
  const walkReason = walkActive ? null : t('clearance.dock.walkHint');

  return (
    <DockFrame
      ref={rootRef}
      testId='clearance-dock'
      testIdPrefix='clearance'
      ariaLabel={t('clearance.dock.title')}
      height={dock.height}
      onHeightChange={setHeight}
      resizeLabel={t('clearance.dock.resize')}
      resizeHelp={t('clearance.dock.resizeHelp')}
      expanded={expanded}
      onEscape={onEscape}
      focusKey={focusKey}
      dragResetKey={openClearanceLayer}
    >
      {/* ---- Header: one 40 px row — which run, a warning if it is stale, the
              verdict and the figures, then what the PANEL does. ---- */}
      <DockHeader
        testId='clearance-header'
        identity={
          <DockIdentity
            eyebrow={t('clearance.dock.eyebrow')}
            title={meta.profile.name}
            titleTestId='clearance-dock-profile'
            scope={t('clearance.dock.scopeAlong', {
              alignment: alignmentName,
              // React escapes at render; i18next escaping on top of it turns
              // "/" and any "&" or apostrophe in an alignment name into
              // literal HTML entities on screen.
              interpolation: { escapeValue: false },
            })}
            scopeTestId='clearance-dock-scope'
            range={rangeText}
            rangeTestId='clearance-range'
            rangeTitle={t('stationVolumes.panel.rangeLabel')}
          />
        }
        // A WARNING, never folded away: "the alignment has changed since this
        // run" is not a parameter, it is a reason to distrust every number
        // beside it.
        warnings={
          alignmentStale && (
            <DockChip tone='warn' testId='clearance-stale-alignment' title={t('clearance.dock.alignmentChangedHelp')}>
              {t('clearance.dock.alignmentChanged')}
            </DockChip>
          )
        }
        kpis={<DockKpiStrip testId='clearance-kpis' items={kpiItems} />}
        buttons={
          <DockPanelButtons
            testIdPrefix='clearance'
            expanded={expanded}
            onToggleExpanded={() => setExpanded(!expanded)}
            onClose={close}
            closeLabel={t('clearance.dock.close')}
            details={
              <DockDetailsPopover
                triggerTestId='clearance-params-toggle'
                testId='clearance-details'
                title={meta.profile.name}
                sections={detailsSections}
                kpis={kpiItems}
              />
            }
          />
        }
      />

      {/* ---- Tab bar: the views, violation stepping, the brush while there
              is one, then the tools. Tab-contextual first (Count | Depth |
              Near miss, on Chart only, and the first thing to go on a narrow
              dock), then the dock-wide ones: View ▾, Export ▾, ⋯. ---- */}
      <DockTabBar
        testIdPrefix='clearance'
        ariaLabel={t('clearance.dock.title')}
        activeId={dock.tab as (typeof TABS)[number]}
        tabs={TABS.map((tab) => ({
          id: tab,
          label: t(`clearance.dock.tab.${tab}`),
          disabledReason: tab === 'chart' && chartTooNarrow ? t('stationVolumes.panel.chartTooNarrow') : null,
        }))}
        onSelect={(tab) => setDock({ ...dock, tab })}
        start={
          <>
            {/* ◀ Go to worst ▶ — `DockStepper`, the section stepper's own
                control, on the LEFT right after the tabs: every station
                stepper in the app is a joined ◀ [value] ▶ on the left (the
                alignment walk toolbar, both docks' cross-sections), and the
                owner read this one, alone on the right among View / Export,
                as a different control. Ungated, like the hotkeys that mirror
                it (decision 4). "Go to worst" is `aria-disabled` on a clean
                run, never the HTML attribute: that dropped it out of the tab
                order and hid its tooltip while ◀ ▶ beside it stayed
                focusable (finding 11). The middle shrinks to its icon below
                1000 px; its group name says it steps VIOLATIONS, which keeps
                it distinct from the Cross-section tab's interval stepper. */}
            <DockToolbarSeparator />
            <DockStepper
              testId='clearance-violation-stepper'
              ariaLabel={t('clearance.dock.violationStepper')}
              prev={{
                testId: 'clearance-violation-prev',
                label: t(prevViolation ? 'clearance.dock.prevViolation' : 'clearance.dock.noPrevViolation'),
                disabled: !prevViolation,
                onClick: () => stepViolation(-1),
              }}
              next={{
                testId: 'clearance-violation-next',
                label: t(nextViolation ? 'clearance.dock.nextViolation' : 'clearance.dock.noNextViolation'),
                disabled: !nextViolation,
                onClick: () => stepViolation(1),
              }}
              center={{
                testId: 'clearance-jump-worst',
                label: t('clearance.dock.jumpWorst'),
                title: t('clearance.dock.jumpWorstHelp'),
                disabled: !worstTarget,
                // The worker recorded the EXACT station of the worst point,
                // so the walk lands ON the point rather than near it. A walk,
                // not a brush: see `moveCursor`.
                onClick: () => {
                  if (worstTarget) moveCursor(worstTarget.station);
                },
              }}
            />
            {chartTooNarrow && (
              <span data-testid='clearance-tab-hint' className={`truncate ${DOCK_TEXT.metaMuted} @max-[560px]:hidden`}>
                {t('stationVolumes.panel.chartTooNarrow')}
              </span>
            )}
            {/* Every KPI silently narrows to the brush, so the brush has to be
                visible and reversible from the dock's own chrome (M4) — and
                only while there is one: "Selection: Whole run" printed on
                every glance said nothing (audit C13). */}
            {selActive && (
              <DockSelectionChip
                range={selectionText}
                onClear={() => setSelection(null)}
                testId='clearance-selection'
                clearTestId='clearance-clear-selection'
              />
            )}
          </>
        }
        right={
          <>
            {dock.tab === 'chart' && json && !error && (
              <div className={`flex shrink-0 items-center ${DOCK_CQ_HIDE.below800}`}>
                <DockToolbar ariaLabel={t('clearance.dock.chartTools')}>
                  <DockSegmented
                    testId='clearance-metric-toggle'
                    ariaLabel={t('clearance.chart.metricGroup')}
                    // Nothing checked while the PASS empty state stands in
                    // for the chart: no metric is being shown.
                    value={showEmptyState ? null : metric}
                    onChange={setMetricChoice}
                    options={CLEARANCE_METRICS.map((m) => ({
                      value: m,
                      label: t(METRIC_KEYS[m].label),
                      title: t(METRIC_KEYS[m].help),
                      testId: `clearance-metric-${m}`,
                    }))}
                  />
                </DockToolbar>
                <DockToolbarSeparator />
              </div>
            )}
            <DockToolbar>
              {/* View ▾ — every display option, as switches that keep the
                  popover open. Gated rows keep today's rules: `aria-disabled`,
                  never `disabled`, a click opens the paywall, an UpgradeBadge
                  on the row. The dot on the trigger says the envelope is on. */}
              <DockViewMenu testId='clearance-view' active={gate.allowed && envelopeOn}>
                <DockMenuSection title={t('clearance.dock.viewSection3d')}>
                  {/* ONE 3D switch, not two: the envelope carries the per-bin
                      verdict, the brush selection and the hover that the flat
                      corridor plates used to. Its key rides under it. */}
                  <DockMenuSwitch
                    testId='clearance-envelope'
                    label={t('clearance.panel.envelope')}
                    description={t('clearance.panel.envelopeHelp')}
                    checked={envelopeOn}
                    onCheckedChange={setEnvelopeOn}
                    gate={gate}
                  >
                    <EnvelopeKey band={outerBand} />
                  </DockMenuSwitch>
                </DockMenuSection>
                <DockMenuSection title={t('clearance.dock.viewSectionColour')}>
                  {/* A radiogroup: a cloud is painted one way at a time.
                      `scalar` is the run's own ramp and needs no
                      classification; the other two colour and filter BY class
                      13, so on a cloud without it they are muted with the
                      reason in their title (never the HTML attribute). */}
                  <DockMenuRadioGroup
                    testId='clearance-view-mode'
                    ariaLabel={t('clearance.panel.viewMode')}
                    value={selectedViewMode}
                    onChange={setViewMode}
                    gate={gate}
                    options={CLEARANCE_VIEW_MODES.map((mode) => ({
                      value: mode,
                      label: t(VIEW_MODE_KEYS[mode].label),
                      title: t(VIEW_MODE_KEYS[mode].help),
                      testId: `clearance-view-mode-${mode}`,
                      disabledReason: mode !== 'scalar' ? viewModeReason : null,
                    }))}
                  />
                </DockMenuSection>
                <DockMenuSection title={t('clearance.dock.viewSectionWalk')}>
                  {/* Two reasons this can be unavailable, answered
                      differently: no walk is a DATA condition (inert, the
                      reason under the label), a locked tier a PAYWALL. Two
                      gates guard it — the analysis and the clip box — and
                      whichever is shut is the one the badge names. */}
                  <DockMenuSwitch
                    testId='clearance-clip-to-bin'
                    label={t('clearance.dock.clipToBin')}
                    description={t('clearance.dock.clipToBinHelp')}
                    checked={clipToBin}
                    onCheckedChange={setClipToBin}
                    gate={gate.allowed ? clipGate : gate}
                    disabledReason={walkReason}
                  />
                  {/* Jump to station — the sibling dock's control, same
                      data condition as Clip to station above. */}
                  <DockMenuSwitch
                    testId='clearance-follow-station'
                    label={t('stationVolumes.section.followStation')}
                    description={t('stationVolumes.section.followStationHelp')}
                    checked={walkActive && jumpToStation}
                    onCheckedChange={() => toggleJumpToStation()}
                    disabledReason={walkReason}
                  />
                </DockMenuSection>
              </DockViewMenu>
              {/* Export ▾. The FILE is the worker's CSV (ruling): re-deriving a
                  download in the browser would be a second implementation of
                  the same table, free to disagree with the file an engineer
                  gets from the batch panel. "Copy as TSV" is the one
                  browser-side export — a clipboard paste, selection-aware,
                  held to the CSV's columns (`clearanceBinsToTsv`). */}
              <DockExportMenu testId='clearance-export'>
                {/* `disabled={!json}` is a LOADING guard, not a gate. */}
                <DockMenuItem data-testid='clearance-export-copy' disabled={!json} onSelect={copyTsv}>
                  {selActive ? t('stationVolumes.export.copySelection') : t('stationVolumes.export.copyAll')}
                </DockMenuItem>
                {csvFilename && (
                  <>
                    <DockMenuSeparator />
                    {csv.url ? (
                      <DockMenuItem asChild>
                        <a
                          data-testid='clearance-download-csv'
                          href={csv.url}
                          download={meta.byproducts.csv}
                          title={t('clearance.dock.downloadCsvTitle')}
                        >
                          {t('clearance.dock.downloadCsv')}
                        </a>
                      </DockMenuItem>
                    ) : (
                      // The filename is registered but its signed URL is not
                      // in hand yet (or the batch query failed). Say which —
                      // translated, the raw error in the tooltip.
                      <DockMenuItem
                        data-testid='clearance-download-csv-pending'
                        disabled
                        className='flex-col items-start gap-0.5'
                        title={csv.error?.message}
                      >
                        <span>{t('clearance.dock.downloadCsv')}</span>
                        <span className={DOCK_TEXT.metaMuted}>
                          {csvError
                            ? t(csvError.key, { status: csvError.status ?? '' })
                            : t('clearance.dock.downloadCsvPending')}
                        </span>
                      </DockMenuItem>
                    )}
                  </>
                )}
                {/* The two PDFs (spec §5.1). `disabled` is a DATA guard, never
                    the gate: the paywall is `onOpenPdfReport`'s. */}
                {!isSharedLink && (
                <>
                <DockMenuSeparator />
                <DockMenuItem
                  data-testid='clearance-export-summary-pdf'
                  aria-disabled={!hasResultsFile || undefined}
                  disabled={!hasResultsFile}
                  onSelect={openSummaryPdf}
                  className='flex-col items-start gap-0.5'
                >
                  <span>{t('clearance.export.summaryPdf')}</span>
                  {!hasResultsFile && (
                    <span className={DOCK_TEXT.metaMuted}>{t('clearance.export.noResultsFile')}</span>
                  )}
                </DockMenuItem>
                <DockMenuItem
                  data-testid='clearance-export-per-station-pdf'
                  aria-disabled={!json || !hasResultsFile || !hasSections || undefined}
                  disabled={!json || !hasResultsFile || !hasSections}
                  onSelect={openPerStationPdf}
                  className='flex-col items-start gap-0.5'
                >
                  <span>{t('clearance.export.perStationPdf')}</span>
                  {!hasResultsFile ? (
                    <span className={DOCK_TEXT.metaMuted}>{t('clearance.export.noResultsFile')}</span>
                  ) : (
                    !hasSections && (
                      <span className={DOCK_TEXT.metaMuted}>{t('clearance.export.perStationPdfNoSections')}</span>
                    )
                  )}
                </DockMenuItem>
                </>
                )}
              </DockExportMenu>
              {/* ⋯ — what does not earn a place in the bar. Its one item is
                  the re-run, so a shared link gets no menu at all. */}
              {!isSharedLink && (
              <DockOverflowMenu testId='clearance-more'>
                <DockMenuItem
                  data-testid='clearance-more-rerun'
                  disabled={!rerunSource}
                  onSelect={openRerun}
                  className='flex-col items-start gap-0.5'
                >
                  <span className='flex items-center gap-1.5'>
                    {t('clearance.dock.rerun')}
                    {!gate.allowed && <UpgradeBadge gate={gate.gate} />}
                  </span>
                  {!rerunSource && (
                    <span className={DOCK_TEXT.metaMuted}>{t('stationVolumes.empty.rerunUnavailable')}</span>
                  )}
                </DockMenuItem>
              </DockOverflowMenu>
              )}
            </DockToolbar>
          </>
        }
      />

      <DockBody id='clearance-tabpanel' labelledBy={`clearance-tab-${dock.tab}`}>
        {/* No swallowed errors: a failed byproduct fetch says what failed and
            offers the retry, rather than showing an empty dock. The house
            `LoadingBadge` renders it (CLAUDE.md); the headline above it names
            the thing that failed, exactly as `StationVolumeDock` does it. */}
        {error && (
          <div data-testid='clearance-load-error' className='flex flex-col items-start gap-2'>
            <p className='text-[13px] font-semibold'>{t('clearance.dock.failed')}</p>
            {/* The raw message only as the tooltip: it is an English GraphQL
                or HTTP string, useful to whoever reports it, not to read. */}
            <div data-testid='clearance-load-error-detail' title={schemaTooNew ? undefined : error.message}>
              <LoadingBadge
                name={badgeName}
                phase='failed'
                error={loadErrorText}
                labels={{ failed: t('clearance.dock.failed') }}
              />
            </div>
            {/* A schema from the future is not a transient failure: only a
                re-run produces a file this client can read, so "Try again"
                would be a button that cannot work — the re-run can. */}
            {schemaTooNew ? (
              rerunControl
            ) : (
              <Button
                data-testid='clearance-retry'
                type='button'
                size='sm'
                variant='outline'
                className='h-7'
                onClick={refetch}
              >
                {t('clearance.dock.retry')}
              </Button>
            )}
          </div>
        )}
        {/* Finding I2: the same multi-megabyte byproduct fetch the sibling dock
            reports with bytes and a progress bar. */}
        {!error && isLoading && !json && (
          <LoadingBadge
            name={badgeName}
            phase={progress?.phase ?? 'fetching'}
            progress={progress?.totalBytes ? progress.loadedBytes / progress.totalBytes : undefined}
            loadedBytes={progress?.loadedBytes}
            totalBytes={progress?.totalBytes ?? undefined}
            labels={{
              fetching: t('clearance.dock.fetching'),
              decoding: t('clearance.dock.decoding'),
              failed: t('clearance.dock.failed'),
            }}
          />
        )}
        {/* Spec §B5, PASS: a run with nothing to plot says so in words, with
            the two ways on, instead of sixty zero bars on a 0–1 axis. The
            verdict chip in the header carries the colour; this is muted. */}
        {!error && json && dock.tab === 'chart' && showEmptyState && (
          <DockEmptyState
            testId='clearance-no-violations'
            tone='pass'
            title={t('clearance.dock.noViolationsTitle')}
            body={
              summary.minMarginM == null
                ? t('clearance.dock.noViolationsBody', { band: outerBand })
                : t('clearance.dock.noViolationsMargin', {
                    margin: formatFixed(summary.minMarginM, 3),
                    band: outerBand,
                  })
            }
            actions={
              <>
                <DockEmptyStateAction testId='clearance-show-near-miss' onClick={() => setMetricChoice('nearMiss')}>
                  {t('clearance.dock.showNearMissChart')}
                </DockEmptyStateAction>
                <DockEmptyStateAction testId='clearance-open-table' onClick={() => setDock({ ...dock, tab: 'table' })}>
                  {t('clearance.dock.openTable')}
                </DockEmptyStateAction>
              </>
            }
          />
        )}
        {!error && json && dock.tab === 'chart' && !showEmptyState && (
          <div ref={plotRef} className='min-h-0 flex-1'>
            <ClearanceChart
              json={json}
              layerId={dock.layerId}
              width={plotBox?.w ?? width - 24}
              height={plotBox?.h ?? DEFAULT_PLOT_H}
              metric={metric}
              onMetricChange={setMetricChoice}
              // The metric control lives in the tab bar (spec §B1), not over
              // the plot — one control, not two.
              // The tab bar's segmented control hides below an 800 px bar
              // (`DOCK_CQ_HIDE.below800`); the chart's own control takes over
              // there, so the metric is never unreachable on a narrow dock.
              showMetricControl={width < 800}
              onWalkTo={moveCursor}
              cursorStation={cursorStation}
            />
          </div>
        )}
        {!error && json && dock.tab === 'table' && (
          <div className='min-h-0 flex-1 overflow-auto'>
            <ClearanceTable json={json} layerId={dock.layerId} onWalkTo={moveCursor} cursorStation={cursorStation} />
          </div>
        )}
        {/* Finding F-7: `meta.byproducts.json` is typed optional and every
            branch above is guarded on `json`, so a run without a registered
            JSON byproduct would otherwise render an empty box with no
            message. The sibling dock's empty state: what is missing, what to
            do, and the control that does it. */}
        {!error && !isLoading && !json && (
          <div data-testid='clearance-no-data' className='flex h-full flex-col items-start gap-2 overflow-auto py-2'>
            <p className='text-[13px] font-semibold'>{t('clearance.dock.noData')}</p>
            <p className='text-slate-500 dark:text-stone-400'>{t('clearance.dock.noDataBody')}</p>
            {rerunControl}
          </div>
        )}
        {!error && json && dock.tab === 'profile' && (
          <div ref={plotRef} className='flex min-h-0 flex-1 items-stretch gap-4'>
            {/* The MEASURED cross-section when the run carries the section
                grid; the static sketch is what a run from a worker older than
                that byproduct still gets, so an old analysis degrades to
                exactly what it always showed rather than to an error. */}
            <ClearanceSectionChart
              json={json}
              meta={meta}
              batchId={batchId}
              binIndex={sectionBinIndex}
              onStepBin={stepSectionBin}
              onLookAt={lookAtSection}
              lookAtReason={walkReason}
              // Where the reader stands, so the section's violation stepper
              // asks the same question the ◀ ▶ chevrons do (review #9).
              cursorStation={cursorStation}
              width={Math.max(240, plotBox?.w ?? width - 24)}
              height={Math.max(160, plotBox?.h ?? DEFAULT_PLOT_H)}
              fallback={
                // A phone-width plot stacks the caption under a full-width
                // sketch: beside it, the flex row squeezed the fixed 320 px
                // SVG and clipped the profile's right half off.
                <div className={cn('flex items-start gap-4', sketchStacked && 'flex-col gap-2')}>
                  <ClearanceProfileSketch
                    vertices={json.profile.vertices}
                    verticalOffsetM={json.params.verticalOffsetM}
                    widthPx={sketchStacked ? Math.max(160, Math.floor(plotBox?.w ?? width - 24)) : 320}
                    heightPx={Math.max(
                      sketchStacked ? 120 : 160,
                      (plotBox?.h ?? DEFAULT_PLOT_H) - (sketchStacked ? SKETCH_CAPTION_H : 0),
                    )}
                    highlight={
                      sectionBin && sectionBin.worstOffsetM !== null && sectionBin.worstHeightM !== null
                        ? { offsetM: sectionBin.worstOffsetM, heightM: sectionBin.worstHeightM }
                        : null
                    }
                  />
                  <p className='max-w-md text-slate-600 dark:text-stone-300'>
                    {sectionBin
                      ? t('clearance.dock.profileSelected', {
                          station: staTag((sectionBin.staStartDisplayM + sectionBin.staEndDisplayM) / 2, 2),
                          depth: sectionBin.maxIntrusionM === null ? '—' : formatFixed(sectionBin.maxIntrusionM, 3),
                        })
                      : t('clearance.dock.profileHint')}
                  </p>
                </div>
              }
            />
          </div>
        )}
      </DockBody>
    </DockFrame>
  );
};
